import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink, PaginationLinks, Icon } from '../../../components';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routeConfiguration';
import classNames from 'classnames';

import Post from './Post/Post';
import SortBy from './SortBy/SortBy';
import css from './Posts.module.css';

// Loading state component
const LoadingState = () => <div className={css.searchInProgressWrapper} />;

// Error state component
const ErrorState = () => (
  <div className={css.searchErrorWrapper}>
    <div className={css.searchError}>
      <Icon name="error" size={48} className={css.searchErrorIcon} />
      <h2 className={css.searchErrorTitle}>
        <FormattedMessage id="CommunityPage.searchPostTitle" />
      </h2>
      <p className={css.searchErrorText}>
        <FormattedMessage id="CommunityPage.searchPostText" />
      </p>
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ returnLink, postsCategory }) => (
  <div className={css.noPostsWrapper}>
    {returnLink}
    <div className={css.noPosts}>
      <Icon name="inbox" size={48} className={css.noPostsIcon} />
      <h2 className={css.noPostsTitle}>
        {postsCategory ? (
          <FormattedMessage
            id="CommunityPage.noPostsTitleWithCategory"
            values={{ category: postsCategory.label }}
          />
        ) : (
          <FormattedMessage id="CommunityPage.noPostsTitle" />
        )}
      </h2>
      <p className={css.noPostsText}>
        <FormattedMessage id="CommunityPage.noPostsText" />
      </p>
    </div>
  </div>
);

// Return link component
const ReturnLink = () => (
  <NamedLink className={css.returnLink} name="CommunityPage">
    <Icon name="chevronLeft" size={14} className={css.returnLinkIcon} />
    <FormattedMessage id="CommunityPage.returnLink" />
  </NamedLink>
);

/**
 * Active-filters pill row — sits between the sort header and the post
 * list. Only renders when at least one filter is applied. Each pill
 * removes its own param on click; "Clear all" wipes every supported
 * filter at once while preserving sort/page params.
 *
 * Reads directly from `currentSearchParams` so it stays in sync with
 * sidebar chip selections without any extra state plumbing.
 */
const FILTER_QUERY_PARAMS = [
  { param: 'pub_category', optionsKey: 'categories', labelKey: 'CommunityPage.categories' },
  { param: 'pub_topics', optionsKey: 'topics', labelKey: 'CommunitySidebar.topicsTitle' },
  {
    param: 'pub_discipline',
    optionsKey: 'disciplines',
    labelKey: 'CommunitySidebar.disciplinesTitle',
  },
  { param: 'pub_role', optionsKey: 'roles', labelKey: 'CommunitySidebar.rolesTitle' },
];

const ActiveFilters = ({ intl, currentSearchParams, history, optionGroups }) => {
  // Resolve each URL value into one or more { groupLabel, key, label }
  // pills. Multi-enum values come through as comma-joined lists.
  const pills = [];
  for (const { param, optionsKey, labelKey } of FILTER_QUERY_PARAMS) {
    const raw = currentSearchParams?.[param];
    if (!raw) continue;
    const options = optionGroups[optionsKey] || [];
    const groupLabel = intl.formatMessage({ id: labelKey });
    raw
      .split(',')
      .filter(Boolean)
      .forEach(key => {
        const match = options.find(o => o.key === key);
        pills.push({ param, key, groupLabel, label: match?.label || key });
      });
  }

  if (pills.length === 0) return null;

  const pushSearch = newParams => {
    history.push(createResourceLocatorString('CommunityPage', routeConfiguration(), {}, newParams));
  };

  // Removing a single pill from a multi-enum keeps the other keys
  // in the comma-list; from a single-enum removes the whole param.
  const removePill = (param, key) => {
    const raw = currentSearchParams[param];
    const remaining = raw
      ? raw
          .split(',')
          .filter(k => k !== key)
          .join(',')
      : '';
    const next = { ...currentSearchParams };
    if (remaining) {
      next[param] = remaining;
    } else {
      delete next[param];
    }
    pushSearch(next);
  };

  const clearAll = () => {
    const next = { ...currentSearchParams };
    FILTER_QUERY_PARAMS.forEach(({ param }) => delete next[param]);
    pushSearch(next);
  };

  return (
    <div className={css.activeFilters}>
      <ul className={css.activeFiltersList}>
        {pills.map(p => (
          <li key={`${p.param}:${p.key}`} className={css.activeFilterPill}>
            <span className={css.activeFilterGroupLabel}>{p.groupLabel}:</span>
            <span className={css.activeFilterLabel}>{p.label}</span>
            <button
              type="button"
              className={css.activeFilterRemove}
              onClick={() => removePill(p.param, p.key)}
              aria-label={intl.formatMessage({ id: 'Posts.removeFilter' }, { label: p.label })}
            >
              <Icon name="close" size={12} />
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className={css.activeFiltersClearAll} onClick={clearAll}>
        <FormattedMessage id="Posts.clearAllFilters" />
      </button>
    </div>
  );
};

// Header component with sort control and create post button
const PostsHeader = ({ intl, currentSearchParams, history, sortByOptions, initialSortValue }) => (
  <div className={css.sortByWrapper}>
    <SortBy
      label={intl.formatMessage({ id: 'Posts.sortBy' })}
      urlParam="sort"
      options={sortByOptions}
      initialValue={initialSortValue}
      onSelect={(urlParam, option) => {
        const newSearchParams = {
          ...currentSearchParams,
          [urlParam]: option,
        };
        history.push(
          createResourceLocatorString('CommunityPage', routeConfiguration(), {}, newSearchParams)
        );
      }}
    />
    <NamedLink className={css.createPostLink} name="NewPostPage">
      <FormattedMessage id="CommunityPage.createPostLink" />
    </NamedLink>
  </div>
);

// Post list component
const PostList = ({
  posts,
  currentUser,
  linkPreviews,
  categories,
  location,
  history,
  addPostCountInProgress,
  addPostCountError,
  onAddPostCount,
}) => (
  <div className={css.posts}>
    {posts.map(post => {
      const matchLinkPreview = linkPreviews.find(p => p.id.uuid === post.id.uuid);
      const linkPreview = matchLinkPreview ? matchLinkPreview.linkPreview : null;
      return (
        <Post
          key={post.id.uuid}
          post={post}
          currentUser={currentUser}
          linkPreview={linkPreview}
          categories={categories}
          location={location}
          history={history}
          addPostCountInProgress={addPostCountInProgress}
          addPostCountError={addPostCountError}
          onAddPostCount={onAddPostCount}
        />
      );
    })}
  </div>
);

// Main Posts component
const Posts = props => {
  const {
    rootClassName,
    className,
    intl,
    posts,
    currentUser,
    location,
    history,
    currentSearchParams,
    searchParamsForPagination,
    pagination,
    categories,
    topics,
    disciplines,
    roles,
    searchPostsInProgress,
    searchPostsError,
    addPostCountInProgress,
    addPostCountError,
    onAddPostCount,
    linkPreviews,
  } = props;

  // Handle loading and error states. Skeleton only when we have no
  // posts to show — post-hydration and filter-change refetches keep
  // the already-rendered feed on screen instead of flashing skeletons.
  if (searchPostsInProgress && (!posts || posts.length === 0)) {
    return <LoadingState />;
  } else if (searchPostsError) {
    return <ErrorState />;
  }

  // Check if posts are loaded and if there are any
  const postsAreLoaded = !searchPostsInProgress && pagination?.totalItems !== null;
  const hasListings = postsAreLoaded && pagination?.totalItems > 0;

  // Get current category
  const postsCategory = currentSearchParams.pub_category
    ? categories.find(c => c.key === currentSearchParams.pub_category)
    : null;

  // Create return link if needed
  const returnLink = postsAreLoaded && postsCategory ? <ReturnLink /> : null;

  // Handle empty state
  if (postsAreLoaded && !hasListings) {
    return <EmptyState returnLink={returnLink} postsCategory={postsCategory} />;
  }

  // Setup sorting options
  const sortByOptions = [
    {
      key: 'meta_voteCount',
      label: intl.formatMessage({ id: 'Posts.SortBy.popular' }),
    },
    {
      key: 'createdAt',
      label: intl.formatMessage({ id: 'Posts.SortBy.newest' }),
    },
    {
      key: '-createdAt',
      label: intl.formatMessage({ id: 'Posts.SortBy.oldest' }),
    },
  ];

  const initialSortValue =
    sortByOptions.find(option => option.key === currentSearchParams.sort)?.key ||
    sortByOptions[0].key;

  // Create pagination component if needed
  const paginationLinks =
    pagination?.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="CommunityPage"
        pageSearchParams={searchParamsForPagination}
        pagination={pagination}
      />
    ) : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <PostsHeader
        intl={intl}
        currentSearchParams={currentSearchParams}
        history={history}
        sortByOptions={sortByOptions}
        initialSortValue={initialSortValue}
      />

      <ActiveFilters
        intl={intl}
        currentSearchParams={currentSearchParams}
        history={history}
        optionGroups={{ categories, topics, disciplines, roles }}
      />

      <PostList
        posts={posts}
        currentUser={currentUser}
        linkPreviews={linkPreviews}
        categories={categories}
        location={location}
        history={history}
        addPostCountInProgress={addPostCountInProgress}
        addPostCountError={addPostCountError}
        onAddPostCount={onAddPostCount}
      />

      {paginationLinks}
    </div>
  );
};

export default Posts;
